// Atelier d'armes : l'arme en 3D, ses quatre emplacements d'accessoires, et ses caractéristiques avant / après.
// Panneau de test (serveur local seulement) : se donner n'importe quel objet ou des crédits.
import * as THREE from 'three';
import { ITEMS } from '../shared/items';
import { countOf, type ItemInst } from '../shared/inventory';
import type { ClientMsg, Place, ProfileView } from '../shared/protocol';
import { MODS, SLOT_NAMES, WEAPONS, modFits, weaponStats, type ModSlot, type Mods, type WeaponStats } from '../shared/weapons';
import { iconFor, iconOf } from './icons';
import { WEAPON_MODELS, buildWeapon, weaponObject } from './weaponModels';

const esc = (s: string) => s.replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
const SLOTS: ModSlot[] = ['optic', 'muzzle', 'mag', 'grip'];
const SLOT_ICON: Record<ModSlot, string> = {
  optic: '<circle cx="12" cy="12" r="7"/><circle cx="12" cy="12" r="1.5"/><path d="M12 2v4M12 18v4M2 12h4M18 12h4"/>',
  muzzle: '<path d="M3 9h12v6H3zM15 10.5h6M15 13.5h6"/>',
  mag: '<path d="M8 3h7l-1 18H9z"/><path d="M8.5 7h6M8.5 11h6"/>',
  grip: '<path d="M8 4h8v5l-2 11h-4L8 9z"/>',
};
const svg = (d: string) => `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${d}</svg>`;

// Caractéristiques affichées : valeur, maximum de la barre, plus haut = mieux ?
const STATS: [string, (w: WeaponStats) => number, number, boolean, (v: number) => string][] = [
  ['Dégâts', w => w.damage * (w.pellets ?? 1), 120, true, v => String(Math.round(v))],
  ['Cadence', w => w.rpm, 900, true, v => `${Math.round(v)} /min`],
  ['Chargeur', w => w.magazine, 60, true, v => String(Math.round(v))],
  ['Stabilité', w => 100 / w.recoilMul, 160, true, v => String(Math.round(v))],
  ['Précision en mouvement', w => 10 / w.moveSpread, 4.5, true, v => v.toFixed(1).replace('.', ',')],
  ['Portée', w => w.range, 120, true, v => `${Math.round(v)} m`],
  ['Visée', w => w.reach, 2.2, true, v => `× ${v.toFixed(2).replace('.', ',')}`],
  ['Discrétion', w => 150 - w.noise, 150, true, v => String(Math.round(v))],
  ['Rechargement', w => w.reload, 4.5, false, v => `${v.toFixed(1).replace('.', ',')} s`],
];

export class Workshop {
  private target: { from: Place; uid: number } | null = null;
  private hover: { slot: ModSlot; mod: string | null } | null = null;
  private renderer: THREE.WebGLRenderer | null = null;
  private scene = new THREE.Scene();
  private camera = new THREE.PerspectiveCamera(24, 1, 0.05, 20);
  private model: THREE.Object3D | null = null;
  private modelKey = '';
  private yaw = 0; // angle choisi à la souris ; la vue oscille doucement autour, toujours de profil
  private drag: number | null = null;
  private el: HTMLElement;

  constructor(private send: (m: ClientMsg) => void, private profile: () => ProfileView | null) {
    this.el = document.createElement('div');
    this.el.id = 'workshop';
    this.el.className = 'wsModal hidden';
    this.el.innerHTML = `<div class="wsBox">
      <header class="wsHead"><span class="wsIco">${svg('<path d="M14.5 6.5a4 4 0 0 0 5 5L12 19a2.1 2.1 0 0 1-3-3l7.5-7.5a4 4 0 0 0-2-2z"/>')}</span><div><small>Atelier d’armes</small><h2 id="wsName"></h2></div><button class="wsClose" title="Fermer (Échap)">✕</button></header>
      <div class="wsBody">
        <div class="wsView"><canvas id="wsCanvas"></canvas><p class="wsHint">Glisser pour tourner l’arme</p></div>
        <aside class="wsStats"><h4>Caractéristiques</h4><div id="wsStats"></div></aside>
      </div>
      <div id="wsSlots" class="wsSlots"></div>
    </div>`;
    document.body.append(this.el);
    this.el.querySelector('.wsClose')!.addEventListener('click', () => this.close());
    this.el.addEventListener('pointerdown', e => { if (e.target === this.el) this.close(); });
    addEventListener('keydown', e => { if (e.code === 'Escape' && this.target) { e.stopImmediatePropagation(); this.close(); } }, true);
    const slots = this.el.querySelector('#wsSlots') as HTMLElement;
    slots.onclick = e => {
      const b = (e.target as HTMLElement).closest('[data-slot]') as HTMLElement | null;
      if (!b || !this.target || (b as HTMLButtonElement).disabled) return;
      this.send({ t: 'mod', from: this.target.from, uid: this.target.uid, slot: b.dataset.slot as ModSlot, mod: b.dataset.mod || null });
    };
    slots.onpointerover = e => {
      const b = (e.target as HTMLElement).closest('[data-slot]') as HTMLElement | null;
      const h = b ? { slot: b.dataset.slot as ModSlot, mod: b.dataset.mod || null } : null;
      if (h?.slot !== this.hover?.slot || h?.mod !== this.hover?.mod) { this.hover = h; this.renderStats(); }
    };
    slots.onpointerleave = () => { this.hover = null; this.renderStats(); };
    const cv = this.el.querySelector('#wsCanvas') as HTMLCanvasElement;
    cv.onpointerdown = e => { this.drag = e.clientX; cv.setPointerCapture(e.pointerId); };
    cv.onpointermove = e => { if (this.drag !== null) { this.yaw += (e.clientX - this.drag) * 0.01; this.drag = e.clientX; } };
    cv.onpointerup = () => { this.drag = null; };
  }

  get isOpen() { return !!this.target; }

  open(from: Place, uid: number) {
    this.target = { from, uid };
    this.el.classList.remove('hidden');
    this.initView();
    this.refresh();
  }
  close() { this.target = null; this.el.classList.add('hidden'); }

  // L'arme visée, où qu'elle soit (elle peut changer de place entre deux mises à jour)
  private item(): ItemInst | null {
    const p = this.profile(), t = this.target;
    if (!p || !t) return null;
    const l = p.loadout;
    const all = [l.primary, l.secondary, ...p.stash.items, ...l.bag.items, ...l.pouch.items];
    return all.find(i => i?.uid === t.uid) ?? null;
  }

  refresh() {
    if (!this.target) return;
    const it = this.item(), p = this.profile();
    const wid = it && ITEMS[it.id]!.weapon;
    if (!it || !wid || !p) { this.close(); return; }
    (this.el.querySelector('#wsName') as HTMLElement).textContent = ITEMS[it.id]!.name;
    const w = WEAPONS[wid];
    // Emplacements : accessoire monté, puis ceux du coffre qui vont sur cette arme
    (this.el.querySelector('#wsSlots') as HTMLElement).innerHTML = SLOTS.map(slot => {
      if (!w.slots.includes(slot)) return `<section class="wsSlot off"><h5>${svg(SLOT_ICON[slot])}${SLOT_NAMES[slot]}</h5><p>Pas d’emplacement sur cette arme.</p></section>`;
      const cur = it.mods?.[slot];
      const options = Object.values(MODS).filter(m => m.slot === slot && modFits(wid, m.id));
      return `<section class="wsSlot${cur ? ' on' : ''}"><h5>${svg(SLOT_ICON[slot])}${SLOT_NAMES[slot]}</h5>
        <div class="wsCur">${cur ? `<img src="${iconOf(cur)}" alt=""><b>${esc(ITEMS[cur]!.name)}</b><button class="wsOff" data-slot="${slot}" data-mod="">Retirer</button>` : '<span class="wsEmpty">Vide</span>'}</div>
        <div class="wsOpts">${options.map(m => {
          const n = countOf(p.stash, m.id), mounted = cur === m.id;
          return `<button class="wsOpt${mounted ? ' mounted' : ''}" data-slot="${slot}" data-mod="${m.id}" ${n && !mounted ? '' : 'disabled'} title="${mounted ? 'Monté' : n ? 'Monter (pris dans le coffre)' : 'Absent du coffre'}">
            <img src="${iconOf(m.id)}" alt=""><span>${esc(ITEMS[m.id]!.short)}</span><small>${mounted ? 'monté' : `× ${n}`}</small></button>`;
        }).join('')}</div></section>`;
    }).join('');
    this.renderStats();
    this.updateModel(wid, it.mods);
  }

  // Barres : valeur actuelle, et aperçu de l'accessoire survolé (vert = mieux, rouge = moins bien)
  private renderStats() {
    const it = this.item(), wid = it && ITEMS[it.id]!.weapon;
    if (!it || !wid) return;
    const now = weaponStats(wid, it.mods);
    let next: WeaponStats | null = null;
    if (this.hover) {
      const m: Mods = { ...it.mods };
      if (this.hover.mod) m[this.hover.slot] = this.hover.mod; else delete m[this.hover.slot];
      next = weaponStats(wid, m);
    }
    (this.el.querySelector('#wsStats') as HTMLElement).innerHTML = STATS.map(([label, f, max, high, fmt]) => {
      const a = f(now), b = next ? f(next) : a;
      const better = high ? b > a + 1e-6 : b < a - 1e-6, worse = high ? b < a - 1e-6 : b > a + 1e-6;
      const pa = Math.min(100, (a / max) * 100), pb = Math.min(100, (b / max) * 100);
      return `<div class="wsStat${better ? ' up' : worse ? ' down' : ''}"><span>${label}</span><b>${fmt(b)}</b>
        <i><u style="width:${Math.min(pa, pb)}%"></u>${pb !== pa ? `<em style="left:${Math.min(pa, pb)}%;width:${Math.abs(pb - pa)}%"></em>` : ''}</i></div>`;
    }).join('') + (now.suppressed || next?.suppressed ? `<p class="wsNote">${next && !next.suppressed ? 'Sans silencieux : ' : ''}Silencieux : tirs étouffés, entendus de beaucoup moins loin.</p>` : '');
  }

  // ---------------------------------------------------------------- Vue 3D

  private initView() {
    if (this.renderer) return;
    const cv = this.el.querySelector('#wsCanvas') as HTMLCanvasElement;
    this.renderer = new THREE.WebGLRenderer({ canvas: cv, antialias: true, alpha: true });
    this.renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.scene.add(new THREE.HemisphereLight(0xe8eef5, 0x3a3632, 2.0));
    const key = new THREE.DirectionalLight(0xfff0dc, 2.4); key.position.set(1, 2, 2); this.scene.add(key);
    const rim = new THREE.DirectionalLight(0xe8a33d, 1.4); rim.position.set(-2, 1, -2); this.scene.add(rim);
    const loop = (now: number) => {
      requestAnimationFrame(loop);
      if (!this.target || !this.model) return;
      const r = cv.getBoundingClientRect();
      if (!r.width) return;
      if (cv.width !== Math.round(r.width * this.renderer!.getPixelRatio())) { this.renderer!.setSize(r.width, r.height, false); this.camera.aspect = r.width / r.height; this.camera.updateProjectionMatrix(); }
      this.model.rotation.y = this.yaw + (this.drag === null ? Math.sin(now / 1800) * 0.45 : 0);
      this.renderer!.render(this.scene, this.camera);
    };
    requestAnimationFrame(loop);
  }
  private updateModel(wid: keyof typeof WEAPONS, mods?: Mods) {
    const key = `${wid}|${JSON.stringify(mods ?? {})}`;
    if (key === this.modelKey) return;
    this.modelKey = key;
    if (this.model) this.scene.remove(this.model);
    // Arme de profil, canon vers la droite, centrée ; la caméra recule selon sa longueur
    const inner = weaponObject(buildWeapon(wid, mods), WEAPON_MODELS[wid]);
    inner.rotation.y = 0;
    const holder = new THREE.Group();
    holder.add(inner);
    const box = new THREE.Box3().setFromObject(holder), c = box.getCenter(new THREE.Vector3());
    inner.position.sub(c);
    this.model = holder;
    this.scene.add(holder);
    const len = Math.max(0.3, box.getSize(new THREE.Vector3()).length());
    this.camera.position.set(0, len * 0.22, len * 1.55);
    this.camera.lookAt(0, 0, 0);
  }
}

// ---------------------------------------------------------------- Panneau de test

const CATS: [string, string][] = [['weapon', 'Armes'], ['mod', 'Accessoires'], ['ammo', 'Munitions'], ['armor', 'Protections'], ['bag', 'Sacs'], ['med', 'Soins'], ['loot', 'Butin']];

export class DevPanel {
  private el: HTMLElement;
  private cat = 'weapon';
  private q = '';
  constructor(private send: (m: ClientMsg) => void) {
    this.el = document.createElement('div');
    this.el.id = 'devPanel';
    this.el.className = 'devPanel hidden';
    this.el.innerHTML = `<header><b>Mode test</b><small>serveur local uniquement</small><button class="devX" title="Fermer (F8)">✕</button></header>
      <div class="devQuick"><button data-give="credits" data-n="100000">+100 000 cr</button><button data-pack="mod">Tous les accessoires</button><button data-pack="weapon">Toutes les armes</button></div>
      <input class="devQ" placeholder="Rechercher un objet" autocomplete="off">
      <nav class="devCats">${CATS.map(([k, l]) => `<button data-cat="${k}"${k === this.cat ? ' class="on"' : ''}>${l}</button>`).join('')}</nav>
      <div class="devList"></div>`;
    document.body.append(this.el);
    this.el.querySelector('.devX')!.addEventListener('click', () => this.toggle(false));
    (this.el.querySelector('.devQ') as HTMLInputElement).oninput = e => { this.q = (e.target as HTMLInputElement).value.trim().toLowerCase(); this.render(); };
    this.el.onclick = e => {
      const b = (e.target as HTMLElement).closest('button') as HTMLButtonElement | null;
      if (!b) return;
      if (b.dataset.cat) { this.cat = b.dataset.cat; this.el.querySelectorAll('.devCats button').forEach(x => x.classList.toggle('on', x === b)); this.render(); }
      else if (b.dataset.give) this.send({ t: 'give', id: b.dataset.give, n: Number(b.dataset.n ?? 1) });
      else if (b.dataset.pack) for (const d of Object.values(ITEMS)) if (d.cat === b.dataset.pack) this.send({ t: 'give', id: d.id, n: 1 });
    };
    addEventListener('keydown', e => { if (e.code === 'F8' && this.enabled) { e.preventDefault(); this.toggle(); } });
    this.render();
  }
  enabled = false;
  toggle(on = this.el.classList.contains('hidden')) { this.el.classList.toggle('hidden', !on); }
  private render() {
    const plain = (t: string) => t.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
    const list = Object.values(ITEMS).filter(d => (this.q ? plain(d.name).includes(plain(this.q)) || d.id.includes(this.q) : d.cat === this.cat));
    (this.el.querySelector('.devList') as HTMLElement).innerHTML = list.map(d => `<div class="devRow"><img src="${iconFor({ id: d.id })}" alt=""><span>${esc(d.name)}<small>${d.id}</small></span>
      <button data-give="${d.id}" data-n="1">+1</button>${d.stack > 1 ? `<button data-give="${d.id}" data-n="${d.stack}">+${d.stack}</button>` : `<button data-give="${d.id}" data-n="3">+3</button>`}</div>`).join('') || '<p class="devNone">Aucun objet.</p>';
  }
  refreshIcons() { this.render(); }
}
